import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsCheck } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import { useError } from '#globalContexts/ErrorContext';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';

import { useEmailCampaignDetailsContext } from '../../../../contexts/EmailCampaignDetailsContext';
import { useApproveAllEmails } from '../../../../models/aiCampaigns/mutations';

const CampaignStatsHeader = ({ campaignId, stats, refetchEmails }) => {
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const approveAllMutation = useApproveAllEmails(campaignId);
	const { showError, showSuccess } = useError();
	const { campaign } = useEmailCampaignDetailsContext();
	const { hasCapability: canSend } = useCapabilityCheck('email.send');

	const handleApproveAll = async () => {
		try {
			await approveAllMutation.mutateAsync(campaign?.campaignStatus);
			refetchEmails();
			setShowConfirmModal(false);
			showSuccess({
				title: 'E-Mails genehmigt',
				message: 'Alle E-Mails wurden erfolgreich genehmigt.',
			});
		} catch {
			showError({
				title: 'Fehler',
				message: 'Die E-Mails konnten nicht genehmigt werden.',
			});
		}
	};

	return (
		<div className="flex items-center justify-between bg-white border rounded-card p-4">
			<div className="flex gap-6 text-sm">
				<div>
					<span className="text-bgBase-800">Gesamt: </span>
					<span className="font-semibold">{stats.total}</span>
				</div>
				<div>
					<span className="text-bgBase-800">Genehmigt: </span>
					<span className="font-semibold text-success-700">{stats.approved}</span>
				</div>
				<div>
					<span className="text-bgBase-800">Ausstehend: </span>
					<span className="font-semibold text-caution-700">{stats.pending}</span>
				</div>
			</div>

			<div className="flex items-center gap-3">
				{stats.pending > 0 && (
					<Button
						onClick={() => setShowConfirmModal(true)}
						disabled={approveAllMutation.isPending || !canSend}
						title={canSend ? '' : 'Versand ist in Ihrem aktuellen Abo nicht verfügbar'}
						variant="outline"
						className="flex items-center gap-2 text-success-700 border-success-700 hover:bg-success-200"
					>
						<BsCheck className="h-5 w-5" />
						{approveAllMutation.isPending ? 'Genehmige...' : 'Alle genehmigen'}
					</Button>
				)}
				{stats.approved === stats.total && stats.total > 0 && (
					<div className="flex items-center gap-2 text-success-700 font-medium">
						<BsCheck className="h-5 w-5" />
						Alle E-Mails genehmigt
					</div>
				)}
			</div>

			<ConfirmModal
				isOpen={showConfirmModal}
				onClose={() => setShowConfirmModal(false)}
				onConfirm={handleApproveAll}
				title="Alle E-Mails genehmigen?"
				message={`Möchten Sie wirklich alle ${stats.pending} ausstehenden E-Mails genehmigen?`}
				confirmText="Alle genehmigen"
				color="green"
				icon={BsCheck}
			/>
		</div>
	);
};

CampaignStatsHeader.propTypes = {
	campaignId: PropTypes.string.isRequired,
	stats: PropTypes.shape({
		total: PropTypes.number.isRequired,
		approved: PropTypes.number.isRequired,
		pending: PropTypes.number.isRequired,
	}).isRequired,
	refetchEmails: PropTypes.func.isRequired,
};

export default CampaignStatsHeader;
